import { useMemo } from 'react';
import { ActivityInboxType } from '../../graphql';
import { useFindUnreadActivityInbox } from '../../activityInbox';
import { ActivityFilterKey, AssetChatSubject } from '../domain';
import { useAssetChatFeed } from './useAssetChatFeed';

// The badge cross-references the sitewide unread inbox against this asset's
// most recent activity, so an unread item older than this window is not
// counted. 20 was low enough to under-report on any busy asset; there is no
// cheap inversion of the lookup because `FindIssuesFilter` has no id/`in`
// filter, so widening the window is the available fix rather than the ideal
// one. The badge can still under-count on an asset with more than this many
// activities newer than an unread one; the feed itself always shows it.
const CHAT_ICON_BADGE_PAGE_SIZE = 100;

export type UseAssetChatUnreadCountArgs = Pick<
  AssetChatSubject,
  'workspaceAssetId' | 'assetId' | 'workspaceId'
> & {
  filterKey: ActivityFilterKey;
  /**
   * A caller that has to call this hook unconditionally (the chat icon,
   * before its own subject resolution finishes) but doesn't yet have a real
   * subject sets this false rather than pass garbage ids into the query.
   * Defaults to true.
   */
  enabled?: boolean;
};

/** `ActivityInbox.data` is untyped JSON server-side; this is the one shape this hook reads off it. */
type UnreadInboxItem = {
  type?: ActivityInboxType;
  data?: { id?: string };
};

/**
 * The chat icon's badge: one per feed ROW carrying unread activity — an
 * incoming chat line or an unread activity card — never the viewer's own
 * posts. Replies inside one thread collapse into that row's single count,
 * which is deliberate: `assetChatFeed` derives its per-row unread dot from
 * the identical id set, so the badge always equals the number of dots the
 * reader will see. Counting inbox rows instead would say 7 while the feed
 * showed 3, with the other 4 a panel deeper. The "never your own" half is
 * already
 * guaranteed server-side — `createInboxForMessage` excludes the poster from
 * the fan-out — so this only has to find which of the sitewide unread inbox
 * belongs to THIS asset, the same cross-reference `activityList.tsx`'s
 * `hasUnread` does for the classic list.
 *
 * `FindIssuesFilter` has no `id`/`in` filter, so there is no cheap "which of
 * these ids are on this asset" query; this reuses the chat feed's own
 * authorized `findIssues` call (a recency window, not the full feed) rather
 * than reach for the unscoped root `Query.findIssues`. An inbox entry older
 * than that window is accepted as a miss for this badge — it still shows in
 * the feed itself once opened. See CHAT_ICON_BADGE_PAGE_SIZE.
 */
export const useAssetChatUnreadCount = ({
  workspaceAssetId,
  assetId,
  workspaceId,
  filterKey,
  enabled = true,
}: UseAssetChatUnreadCountArgs): { count: number; isLoading: boolean } => {
  const { data: inboxData, isLoading: isInboxLoading } =
    useFindUnreadActivityInbox();
  const { items, isLoading: isFeedLoading } = useAssetChatFeed({
    workspaceAssetId,
    assetId,
    workspaceId,
    filterKey,
    pageSize: CHAT_ICON_BADGE_PAGE_SIZE,
    enabled,
  });

  const count = useMemo(() => {
    const inboxItems: UnreadInboxItem[] = inboxData?.items ?? [];
    if (inboxItems.length === 0 || items.length === 0) return 0;

    const unreadIssueIds = new Set(
      inboxItems
        .filter(
          (i) =>
            i.type === ActivityInboxType.IssueUpdated ||
            i.type === ActivityInboxType.IssueMessageCreated
        )
        .map((i) => i.data?.id)
    );

    return items.filter((item) => unreadIssueIds.has(item.id)).length;
  }, [inboxData, items]);

  return { count, isLoading: isInboxLoading || isFeedLoading };
};
